import { Flex, Text } from '@chakra-ui/react';
import { faFile } from '@fortawesome/pro-light-svg-icons';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Badge, Banner, OutlineCard } from '@morressier/alembic';
import { getRevisionSeverityText } from '@publishing-engine/domain/models';
import { IGetRevisionHistory } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import dateFormat from 'dateformat';
import { Fragment } from 'react';
import {
  getReviewHistoryAccordionSubTitle,
  getReviewHistoryAccordionTitle
} from 'src/components/Submission/ReviewTab/ReviewRoundHistory/ReviewsHistory';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { formatDate } from 'src/utils/date';
import MultilineText from 'src/components/MultilineText';
import FileViewerButtonText from 'src/components/Submission/FileViewerButtonText';

const revisionOverviewContent = (
  revision: IGetRevisionHistory,
  submissionContextId?: string
) => {
  const reviewerFeedback = !revision.reviewerRevisionFeedback?.length
    ? revision.reviewerOriginalFeedback ?? []
    : revision.reviewerRevisionFeedback;

  return (
    <Flex flexDirection="column" gap={6}>
      <Text textStyle="headingSection">Revision Request</Text>
      {revision.type === 'transfer' && (
        <Banner variant="attention" heading="Transfer Offer" />
      )}
      {revision.severity ? (
        <Banner
          variant="attention"
          heading={`${getRevisionSeverityText(revision.severity)} Requested`}
        />
      ) : (
        revision.type === 'review' && (
          <Banner variant="attention" heading={'Revision Requested'} />
        )
      )}
      {revision.dateEntered && (
        <Flex gap={2} alignItems="center">
          <FontAwesomeIcon icon={faCalendar} />
          <Text textStyle="textBody">Received</Text>
          <Text textStyle="textBody">{dateFormat(revision.dateEntered)}</Text>
        </Flex>
      )}
      {revision.editorialComment && (
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">Comments from the Editor</Text>
          <MultilineText
            textStyle="textBody"
            text={revision.editorialComment}
          />
        </Flex>
      )}
      {revision.editorialAttachments &&
        revision.editorialAttachments.length > 0 && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingGroup">Attachments from the Editor</Text>
            <Flex flexDirection="column" gap={0}>
              {' '}
              {revision.editorialAttachments.map(file => (
                <Flex justifyContent="space-between" key={file?.referenceName}>
                  <Flex gap={2} alignItems="center">
                    <FontAwesomeIcon icon={faFile} />
                    <Text textStyle="textBody">{file?.referenceName}</Text>
                    <FileViewerButtonText
                      file={file}
                      submissionContextId={submissionContextId}
                      textStyle="textBody"
                      flexShrink={0}
                    >
                      View
                    </FileViewerButtonText>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>
        )}

      {reviewerFeedback.map((feedback, index) => {
        return (
          <Flex flexDirection="column" gap={6}>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">
                Feedback from Reviewer {index + 1}
                {feedback?.reviewer?.firstName && (
                  <>
                    {' ('}
                    {feedback?.reviewer.firstName} {feedback?.reviewer.lastName}
                    {')'}
                  </>
                )}
              </Text>
              <Text textStyle="textBody">{feedback.comment}</Text>
            </Flex>
            {(feedback.attachments || [])?.map(item => (
              <Flex
                padding={4}
                borderBottom="1px"
                borderColor="neutralPrimaryBorder"
                _last={{ borderBottom: 'none' }}
                justifyContent="space-between"
              >
                <Flex gap={2} alignItems="center">
                  <FontAwesomeIcon icon={faFile} fontSize={24} />
                  <Text
                    textStyle="textBody"
                    wordBreak={'break-word'}
                    whiteSpace={'normal'}
                  >
                    {item.name}
                  </Text>
                </Flex>
                <FileViewerButtonText
                  file={item}
                  submissionContextId={submissionContextId}
                  textStyle="textBody"
                  flexShrink={0}
                >
                  View
                </FileViewerButtonText>
              </Flex>
            ))}
          </Flex>
        );
      })}
      {revision.authorResponse && (
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">My Response</Text>
          <Text textStyle="headingItem">Comment</Text>
          <Text textStyle="textBody">{revision.authorResponse}</Text>
          {revision.authorResponseAttachments &&
            revision.authorResponseAttachments.length > 0 && (
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingItem">Attachment</Text>
                <Flex flexDirection="column" gap={0}>
                  {' '}
                  {revision.authorResponseAttachments.map(file => (
                    <Flex
                      justifyContent="space-between"
                      key={file?.referenceName}
                    >
                      <Flex gap={2} alignItems="center">
                        <FontAwesomeIcon icon={faFile} />
                        <Text textStyle="textBody">{file?.referenceName}</Text>
                        <FileViewerButtonText
                          file={file}
                          submissionContextId={submissionContextId}
                          textStyle="textBody"
                          flexShrink={0}
                        >
                          View
                        </FileViewerButtonText>
                      </Flex>
                    </Flex>
                  ))}
                </Flex>
              </Flex>
            )}
        </Flex>
      )}
    </Flex>
  );
};

export const ReviewHistoryForAuthor = () => {
  const { revisionHistory, submissionContextId } = useSubmissionData();

  if (
    !(
      revisionHistory?.current.rounds.length || revisionHistory?.previous.length
    )
  ) {
    return null;
  }

  const { current, previous } = revisionHistory;

  const renderRevisionRoundGroup = (
    revisionGroup: {
      journalName: string;
      rounds: IGetRevisionHistory[];
      dateEntered?: string;
    },
    isCurrent: boolean,
    index: number = 0,
    submissionContextId?: string
  ) => {
    const { journalName, rounds, dateEntered } = revisionGroup;
    const journalNameUpdated =
      index === 0 && previous.length === 1
        ? current.journalName
        : previous[index - 1]?.journalName ?? current.journalName;

    return (
      <>
        <Flex flexDirection="column" gap={2}>
          <Flex justifyContent="space-between">
            <Text textStyle="textBody" fontWeight="semibold">
              {journalName} Feedback
            </Text>
            <Badge variant={isCurrent ? 'attention' : 'neutral'} size="context">
              {isCurrent ? 'Current Journal' : 'Transferred'}
            </Badge>
          </Flex>
          {!isCurrent && (
            <Text textStyle="textBody">
              Transferred to {journalNameUpdated} on{' '}
              {formatDate(new Date(dateEntered ?? ''))}
            </Text>
          )}
        </Flex>
        <Accordion
          data-testId="revisions-history-overview-author"
          chakraAccordionProps={{
            allowMultiple: true,
            defaultIndex: [],
            style: {
              display: 'flex',
              flexDirection: 'column'
            }
          }}
          accordionItemProps={{
            marginBottom: 0
          }}
          items={rounds?.map((revision, index) => {
            const round = rounds.length - index;
            return {
              title: getReviewHistoryAccordionTitle(revision, round),
              subtitle: getReviewHistoryAccordionSubTitle(
                rounds,
                revision,
                index
              ),
              content: revisionOverviewContent(revision, submissionContextId)
            };
          })}
        />
      </>
    );
  };

  return (
    <OutlineCard borderless width="100%">
      <Flex flexDirection="column" gap={4}>
        <Text textStyle="headingSection">Reviews History</Text>
        <Text textStyle="textBody">
          View peer review feedback across multiple rounds - all rounds will be
          included, even from previous journals from which this submission was
          transferred.
        </Text>
        {!!current.rounds.length &&
          renderRevisionRoundGroup(current, true, 0, submissionContextId)}
        {previous.map((revisionGroup, index) => (
          <Fragment key={revisionGroup.id}>
            {renderRevisionRoundGroup(
              revisionGroup,
              false,
              index,
              submissionContextId
            )}
          </Fragment>
        ))}
      </Flex>
    </OutlineCard>
  );
};
